<script setup lang="ts">
import { defineProps, defineEmits } from 'vue'
import { NCard, NImage, NTag, NButton, NIcon } from 'naive-ui'
import { ChevronLeftRound } from '@vicons/material'
import { getImgUrl } from '@/ts/helpers'
import { TalentProfileProps } from "@/ts/interfaces";

const props = defineProps<TalentProfileProps>()

const experienceYearsText = props.yearsOfExperience > 1 ? 'Years' : 'Year'

const emit = defineEmits(['expandCard', 'closeCard'])

const expandCard = () => emit('expandCard')

const closeCard = () => emit('closeCard')

</script>

<template>
  <n-card size="large" v-if="!isExpanded" class="talent-card">
    <div class="card-header grid-x align-bottom">
      <div class="cell large-3 flex-container">
        <n-image :src="getImgUrl(profilePicLink)"/>
      </div>
      <div class="cell large-8 small-offset-1">
        <p class="body-text-md text-w-bold">
          {{ name }}
        </p>
        <a class="simple-link" @click="expandCard">View profile</a>
      </div>
    </div>

    <div class="card-content  talent-card-info">
      <div class="data-set">
        <p class="data-key text-w-bold">Main Skills:</p>
        <p class="data-value main-skills-container grid-x">
          <n-tag v-for="(skill, key) in mainSkills" :key="key" type="info" :bordered="false">
            {{ skill }}
          </n-tag>
        </p>
      </div>
      <div class="data-set">
        <p class="data-key text-w-bold">Years of experience:</p>
        <p class="data-value">
          {{ yearsOfExperience }} {{ experienceYearsText }}
        </p>

      </div>
      <div class="data-set">
        <p class="data-key text-w-bold">Location:</p>
        <p class="data-value">
          {{ location }}
        </p>

      </div>
      <div class="data-set">
        <p class="data-key text-w-bold">Hourly rate:</p>
        <p class="data-value">${{ hourlyRate }} USD</p>
      </div>
    </div>

    <n-button type="primary" :focusable="false" size="large" @click="expandCard">
      VIEW PROFILE
    </n-button>

  </n-card>
  <n-card size="large" v-else class="talent-card expanded">
    <div class="grid-x">
      <div class="cell small-4 position-relative">
        <div class="height-100 flex-container flex-dir-column align-center-middle">
          <div class="grid-x flex-dir-column text-center">
            <div class="cell large-6 small-offset-3">
              <div class="image-wrap flex-container">
                <n-image width="100%" :src="getImgUrl(profilePicLink)" />
              </div>
            </div>
            <p>{{ name }}</p>
            <span>Profile Details</span>
          </div>
        </div>
        <div class="card-divider"></div>
      </div>
      <div class="cell small-4 card-content talent-card-info">
        <div class="data-set">
          <p class="data-key text-w-bold">About the candidate:</p>
          <p class="data-value text-transform-none">
            {{ about }}
          </p>
        </div>
        <div class="data-set">
          <p class="data-key text-w-bold">Main Skills:</p>
          <p class="data-value main-skills-container grid-x">
            <n-tag v-for="(skill, key) in mainSkills" :key="key" type="info" :bordered="false">
              {{ skill }}
            </n-tag>
          </p>
        </div>
        <div class="data-set">
          <p class="data-key text-w-bold">Complementary Skills:</p>
          <p class="data-value main-skills-container grid-x">
            <n-tag v-for="(skill, key) in mainSkills" :key="key" type="info" :bordered="false">
              {{ skill }}
            </n-tag>
          </p>
        </div>
      </div>
      <div class="cell small-4 card-content talent-card-info">
        <div class="data-set">
          <p class="data-key text-w-bold">Experience:</p>
          <div class="data-value text-transform-none">
            <ul v-for="(job, index) in experience" :key="index">
              <li>{{ job.job_position }}</li>
              <li>({{ job.time_period }})</li>
            </ul>
          </div>
        </div>
        <div class="data-set">
          <p class="data-key text-w-bold">Years of experience:</p>
          <p class="data-value">
            {{ yearsOfExperience }} {{ experienceYearsText }}
          </p>

        </div>
        <div class="data-set">
          <p class="data-key text-w-bold">Location:</p>
          <p class="data-value">
            {{ location }}
          </p>
        </div>
        <div class="grid-x flex-y-end">
          <div class="cell auto">
            <div class="data-set">
              <p class="data-key text-w-bold">Hourly rate:</p>
              <p class="data-value">${{ hourlyRate }} USD</p>
            </div>
          </div>
          <div class="cell auto">
            <a class="simple-link flex-container align-center-middle font-style-normal text-transform-none" @click.prevent="closeCard">
              <n-icon>
                <chevron-left-round></chevron-left-round>
              </n-icon>
              Back
            </a>
          </div>
        </div>
      </div>
    </div>
  </n-card>
</template>


<style lang="scss">
.talent-card {
  &.expanded {
    .card-content {
      margin-top: 0;
      margin-bottom: 0;
      padding-left: 30px;

      .data-set {
        margin-top: 18px;
      }
    }

    .card-divider {
      top: 0;
      right: 0;
      width: 3px;
      height: 100%;
      position: absolute;
      border-radius: 50px;
      background: $border-color-gradient-0-degree;
    }

    .image-wrap {
      padding: 6px;
      background: url("@/assets/utils/gradient-circle.png") center;
      background-size: 100%;
    }
  }

  .card-content {
    margin-top: 36px;
    margin-bottom: 22px;

    .data-set {
      margin-top: 30px;

      .data-value.main-skills-container {
        gap: 10px;
      }
    }
  }
}
</style>